import React from "react";
import { IconButton, Tooltip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { AbsenceRequestResponseDto } from "../../../types/absence";
import { AbsenceRequestStatus } from "../../../constants/absenceEnums";
import { DataTable, Column, Action } from "../../../components/common/DataTable/DataTable";
import {
  AbsenceCategoryChip,
  AbsenceStatusChip,
  DateRangeDisplay,
} from "../../../components/absence";
import { getAbsenceCategoryColor, getAbsenceCategoryName } from "../../../utils/statusColors";

interface PendingRequestsListProps {
  requests: AbsenceRequestResponseDto[];
  isLoading: boolean;
  onApproveClick: (request: AbsenceRequestResponseDto) => void;
  onDenyClick: (request: AbsenceRequestResponseDto) => void;
  onViewDetailsClick: (request: AbsenceRequestResponseDto) => void;
  totalCount?: number;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

const PendingRequestsList: React.FC<PendingRequestsListProps> = ({
  requests,
  isLoading,
  onApproveClick,
  onDenyClick,
  onViewDetailsClick,
  totalCount,
  hasNextPage,
  isLoadingMore,
  onLoadMore,
}) => {
  const truncateText = (text: string | null, maxLength: number = 50): string => {
    if (!text) return "-";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };
  const columns: Column[] = [
    {
      field: "employeeName",
      headerName: "Employee",
      flex: 1.5,
    },
    {
      field: "absenceCategory",
      headerName: "Type",
      flex: 1,
      renderCell: (value: number) => (
        <span style={{ 
          color: getAbsenceCategoryColor(value),
          fontWeight: 'bold'
        }}>
          {getAbsenceCategoryName(value)}
        </span>
      ),
    },
    {
      field: "startDate",
      headerName: "Dates",
      flex: 2,
      renderCell: (value: string, row: AbsenceRequestResponseDto) => (
        <DateRangeDisplay
          startDate={row.startDate}
          endDate={row.endDate}
          days={row.daysDeducted}
        />
      ),
    },
    {
      field: "requestReason",
      headerName: "Reason",
      flex: 1.5,
      renderCell: (value: string | null) => truncateText(value, 40),
    },
    {
      field: "createdAt",
      headerName: "Requested On",
      flex: 1,
      renderCell: (value: string) => new Date(value).toLocaleDateString(),
    },
  ];

  const getActions = (
    request: AbsenceRequestResponseDto
  ): Action<AbsenceRequestResponseDto>[] => {
    const actions: Action<AbsenceRequestResponseDto>[] = [];

    // Always show view details button
    actions.push({
      icon: <VisibilityIcon />,
      label: "View Details",
      onClick: onViewDetailsClick,
      color: "primary",
    });

    // Only show approve/deny actions for pending requests
    if (request.status === AbsenceRequestStatus.Pending) {
      actions.push(
        {
          icon: <CheckCircleIcon />,
          label: "Approve",
          onClick: onApproveClick,
          color: "success",
        },
        {
          icon: <CancelIcon />,
          label: "Deny",
          onClick: onDenyClick,
          color: "error",
        }
      );
    }

    return actions;
  };

  return (
    <DataTable
      items={requests}
      columns={columns}
      actions={getActions}
      isLoading={isLoading}
      totalCount={totalCount}
      hasNextPage={hasNextPage}
      isLoadingMore={isLoadingMore}
      onLoadMore={onLoadMore}
    />
  );
};

export default PendingRequestsList;

